import React from 'react';
import { MediaLibraryItem } from '../../../api/media';
import { constructMediaUrl } from '../../../api/helpers';
import { mediaLabel, resolveThumbSources } from './mediaCardUtils';

/**
 * The lineage of an open media item, as resolved by the backend:
 * - `parent`     — the item this one was derived from (if any).
 * - `siblings`   — other items sharing this item's parent (co-variations).
 * - `variations` — items derived *from* this item.
 * All three are optional / possibly-empty; a generative-only concept that is
 * simply absent for uploads and legacy rows.
 */
export interface RelatedMedia {
  parent: MediaLibraryItem | null;
  siblings: MediaLibraryItem[];
  variations: MediaLibraryItem[];
}

interface MediaLineageProps {
  related?: RelatedMedia;
  loading?: boolean;
  onNavigate?: (mediaId: string) => void;
}

const LineageThumb: React.FC<{
  item: MediaLibraryItem;
  onNavigate?: (mediaId: string) => void;
}> = ({ item, onNavigate }) => {
  const mediaId = item.media_id || item.id;
  const label = mediaLabel(item);
  const sources = React.useMemo(() => resolveThumbSources(item), [item]);
  // Prefer the lightweight thumbnail; fall back to the micro data-URI, then a
  // neutral fallback cell. The micro-thumb is a base64 data-URI (P2) so it is
  // safe to use directly as a src without going through constructMediaUrl.
  const thumb =
    sources.thumbnailUrl ||
    (item.micro_thumb ? item.micro_thumb : '') ||
    (item.url ? constructMediaUrl(item.url) : '');

  return (
    <button
      type="button"
      className="media-lineage-thumb"
      onClick={() => onNavigate?.(mediaId)}
      disabled={!onNavigate}
      title={label}
      aria-label={`Open ${label}`}
    >
      {thumb ? (
        <img src={thumb} alt={label} loading="lazy" />
      ) : (
        <span className="media-lineage-fallback">{(item.type || 'file').slice(0, 3).toUpperCase()}</span>
      )}
    </button>
  );
};

/**
 * Renders the parent / sibling / variation thumbnails for the inspector, each
 * click-to-navigate. Surfaces only the groups that actually exist; renders
 * nothing at all when there is no lineage to show (so it is invisible for
 * uploads and items that have neither a parent nor any derivatives).
 */
const MediaLineage: React.FC<MediaLineageProps> = ({ related, loading = false, onNavigate }) => {
  if (loading) {
    return (
      <div>
        <p className="attachment-meta font-medium mb-1">Lineage</p>
        <p className="attachment-meta">Loading lineage…</p>
      </div>
    );
  }

  if (!related) return null;
  const { parent, siblings, variations } = related;
  const hasAny = !!parent || siblings.length > 0 || variations.length > 0;
  if (!hasAny) return null;

  return (
    <div>
      <p className="attachment-meta font-medium mb-1">Lineage</p>
      <div className="media-lineage-groups">
        {parent && (
          <div className="media-lineage-group">
            <span className="media-lineage-label">Parent</span>
            <div className="media-lineage-row">
              <LineageThumb item={parent} onNavigate={onNavigate} />
            </div>
          </div>
        )}
        {variations.length > 0 && (
          <div className="media-lineage-group">
            <span className="media-lineage-label">
              {variations.length} {variations.length === 1 ? 'Variation' : 'Variations'}
            </span>
            <div className="media-lineage-row">
              {variations.map((it) => (
                <LineageThumb key={it.media_id || it.id} item={it} onNavigate={onNavigate} />
              ))}
            </div>
          </div>
        )}
        {siblings.length > 0 && (
          <div className="media-lineage-group">
            <span className="media-lineage-label">
              {siblings.length} {siblings.length === 1 ? 'Sibling' : 'Siblings'}
            </span>
            <div className="media-lineage-row">
              {siblings.map((it) => (
                <LineageThumb key={it.media_id || it.id} item={it} onNavigate={onNavigate} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MediaLineage;
